<template>
  <AppContent>
    <b-loading data-cy="LOADING" isFullPage :active="isLoading" />
    <div v-if="process">
      <div class="wrapper-header">
        <div class="header-item-left cursor-pointer" @click="listProcesses()">
          <b-icon
            :type="isContrast ? 'is-white' : 'is-blue-oil'"
            icon="arrow-left-thick"
            size="is-medium"
          />
        </div>
        <div class="header-item-right">
          <div class="is-flex is-align-items-center">
            <label
              data-cy="PROCESS_DETAILS_NUP"
              class="is-size-6 has-text-weight-semibold"
            >
              {{ General.NUP }}: {{ applyMaskNUP(process.nup) }}
            </label>
            <b-tooltip
              v-if="isConfidential"
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
              position="is-top"
              :label="secrecyName"
              class="ml-5"
              role="tooltip"
            >
              <div class="circle-red">
                <b-icon type="is-white" icon="lock" customSize="mdi-inherit" />
              </div>
            </b-tooltip>
            <b-tooltip
              v-if="isAppenderProcess || isAppendedProcess"
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
              position="is-right"
              :label="
                isAppenderProcess
                  ? General.TEXTS.PROCESS_APPENDER
                  : General.TEXTS.PROCESS_APPEND
              "
              class="ml-5"
              role="tooltip"
            >
              <b-icon
                :type="isContrast ? 'is-white' : 'is-blue-oil'"
                icon="link-variant"
              />
            </b-tooltip>
          </div>
          <div v-if="process.subject" class="is-size-7">
            {{ process.subject.name }}
          </div>
        </div>
        <div v-if="!isMobile" class="header-item-actions">
          <div class="buttons is-justify-content-flex-end">
            <b-tooltip
              v-if="canShowArchived"
              multilined
              class="mr-5"
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              position="is-left"
              size="is-large"
              role="tooltip"
              :always="showPDFTooltip && !isProcessOpennedExternal"
            >
              <template v-slot:content>
                <p v-if="isProcessOpennedExternal" class="has-text-left">
                  {{ getViewTooltipLabelExternal }}
                </p>
                <section
                  v-else
                  class="has-text-left"
                  @mouseout="setShowPDFTooltip(false)"
                  @mousemove="setShowPDFTooltip(true)"
                >
                  <p class="has-text-color-1 has-text-weight-bold">
                    {{ App.ProcessViewer.VIEW_DOCUMENTS }}
                  </p>
                  {{ App.ProcessViewer.MESSAGE_NEW_VIEWER }}
                </section>
              </template>
              <b-button
                :type="isContrast ? 'is-white' : 'is-green-lawn'"
                iconLeft="folder-search"
                role="button"
                :disabled="isProcessOpennedExternal"
                :aria-description="General.ARIA_LABEL.VIEW_DOCUMENT"
                @click.stop="getCompleteProcessFileStream(process.id)"
                @mouseover="setShowPDFTooltip(true)"
                @mousemove="setShowPDFTooltip(false)"
              >
                <label class="spacing-px-5">
                  {{ General.TO_VIEW }}
                </label>
              </b-button>
            </b-tooltip>
            <b-tooltip
              v-if="canArchiveOrUnarchive"
              multilined
              class="mr-5"
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              size="is-large"
              position="is-top"
              :label="General.TEXTS.UNABLE_TO_UNARCHIVED_SINGULAR"
              :active="!inActualCapacity && process.is_archived"
              role="tooltip"
            >
              <b-button
                :type="isContrast ? 'is-white' : 'is-green-lawn'"
                :iconLeft="
                  process.is_archived
                    ? `archive-arrow-up`
                    : `archive-arrow-down`
                "
                :disabled="!inActualCapacity && process.is_archived"
                role="button"
                :aria-description="General.ARIA_LABEL.ARCHIVE"
                @click="archiveProcess()"
              >
                <label>
                  {{
                    process.is_archived
                      ? General.UNARCHIVE_PROCESS
                      : General.ARCHIVE_PROCESS
                  }}
                </label>
              </b-button>
            </b-tooltip>
            <b-button
              v-if="
                validateResponsable &&
                  !isAppendedProcess &&
                  !process.is_archived &&
                  process.can_tramit
              "
              :type="isContrast ? 'is-white' : 'is-green-lawn'"
              iconLeft="account-arrow-right"
              role="button"
              :aria-description="General.ARIA_LABEL.CHANGE_RESPONSIBLE"
              @click="checkSecrecyOnAssignResponsible"
            >
              <label class="spacing-px-5">
                {{
                  process.user_responsible_id
                    ? General.CHANGE_RESPONSIBLE
                    : General.ASSIGN_RESPONSIBLE
                }}
              </label>
            </b-button>
            <b-button
              v-if="hasForwardPermission && !isProcessOpennedExternal"
              :type="isContrast ? 'is-white-dark' : 'is-green-lawn'"
              iconLeft="send"
              role="button"
              @click.stop="checkDispathFilled(openModalForwardProcess)"
            >
              <label>
                {{ General.FORWARD }}
              </label>
            </b-button>
            <b-tooltip
              v-if="hasOriginProcessAvailable && process.can_tramit"
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              :position="isMobile ? 'is-top' : 'is-left'"
              :label="General.TEXTS.NOT_PROCESS_ORIGIN"
              :active="!inActualCapacity"
              class="mr-5"
              role="tooltip"
            >
              <b-button
                :type="isContrast ? 'is-white' : 'is-green-lawn'"
                iconLeft="send"
                role="button"
                :aria-description="General.ARIA_LABEL.PROCESSING"
                :disabled="!inActualCapacity"
                @click.stop="openModalReturnToOrigin()"
              >
                <label>{{ General.TEXTS.PROCESSED_ORIGIN }}</label>
              </b-button>
            </b-tooltip>
            <br />
            <b-tooltip
              v-if="canShowArchived"
              multilined
              class="mr-5"
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              size="is-large"
              :position="isMobile ? 'is-top' : 'is-left'"
              :label="
                process.can_tramit
                  ? General.ARIA_LABEL.VIEW_OCCURRENCY
                  : getViewTooltipLabelExternalSystem
              "
              role="tooltip"
            >
              <b-button
                :type="isContrast ? 'is-white' : 'is-green-lawn'"
                iconLeft="file-search"
                role="button"
                :aria-description="General.ARIA_LABEL.VIEW_OCCURRENCY"
                :disabled="!process.can_tramit"
                @click.stop="checkIsConfidential(downloadOccurrency)"
              >
                <label>{{ General.OCCURENCY_FILE_PLURAL }}</label>
              </b-button>
            </b-tooltip>
            <b-tooltip
              v-if="showDocumentsTab"
              multilined
              class="mr-5"
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              size="is-large"
              :position="isMobile ? 'is-top' : 'is-left'"
              role="tooltip"
            >
              <template v-slot:content>
                <section v-if="process.can_tramit" class="has-text-left">
                  <p class="has-text-color-1 has-text-weight-bold">
                    {{ App.ProcessViewer.VIEW_COMPLETE_FILE }}
                  </p>
                  {{ App.ProcessViewer.MESSAGE_COMPLETE_FILE }}
                </section>
                <section v-else class="has-text-left">
                  {{ getViewTooltipLabelExternalSystem }}
                </section>
              </template>
              <b-button
                :type="isContrast ? 'is-white' : 'is-green-lawn'"
                iconLeft="file-pdf-box"
                role="button"
                :disabled="!process.can_tramit"
                @click="checkIsConfidential(putProcessInQueue, false)"
              >
                <label>{{ App.ProcessViewer.COMPLETE_FILE }}</label>
              </b-button>
            </b-tooltip>
          </div>
        </div>
        <div>
          <ActionsGrouper
            v-if="isMobile"
            :actions="actionsList()"
            :defaultDotsMaxActions="0"
          />
        </div>
      </div>
      <div v-if="Boolean(getSignatureHistory.length)" class="new-action">
        <div class="is-flex is-align-items-center">
          <b-icon
            class="spacing-mx-10"
            type="is-primary"
            icon="alert-circle-outline"
            size="is-small"
          />
          <p class="has-text-color-7">
            <a
              class="has-text-color-7 is-underlined"
              @click="openModalDetail"
              >{{ General.CLICK_HERE }}</a
            >
            {{ General.MESSAGES.NEW_ACTIONS }}
          </p>
        </div>
        <a @click="newDetailReaded">
          <b-icon icon="close" type="is-black" />
        </a>
      </div>
      <div v-if="showMessageWithdrawal" class="new-action red">
        <div class="is-flex is-align-items-center">
          <b-icon
            class="spacing-mx-10"
            type="is-red"
            icon="alert-circle-outline"
            size="is-small"
          />
          <p class="has-text-color-7">
            {{ General.MESSAGES.WITHDRAWALS_1 }}
            <a class="has-text-color-7 is-underlined" @click="archiveProcess">{{
              General.CLICK_HERE
            }}</a>
            {{ General.MESSAGES.WITHDRAWALS_2 }}
          </p>
        </div>
        <a @click="closeWithdrawalMessage">
          <b-icon icon="close" type="is-black" />
        </a>
      </div>
      <b-tabs
        v-model="activeTab"
        class="block tabs-mobile remove-padding-x"
        :animated="animated"
        :expanded="expanded"
        :multiline="multiline"
        role="tab"
      >
        <b-tab-item :value="TABS.BASIC_DATA.value">
          <template slot="header">
            <label class="tabs-title spacing-px-10">
              {{ App.ProcessDetailsScreen.BASIC_DATA }}
            </label>
          </template>
          <ProcessData
            v-if="!isLoading"
            :process="process"
            :bookmarkList="bookmarks"
          />
        </b-tab-item>

        <b-tab-item v-if="showDispatchTab" :value="TABS.ORDERS.value">
          <template slot="header">
            <label class="tabs-title spacing-px-10" data-cy="DISPATCH_TAB">
              {{ App.ProcessDetailsScreen.ORDERS }}
            </label>
          </template>
          <DispatchProcessTab :id="id" />
        </b-tab-item>

        <b-tab-item v-if="showDocumentsTab" :value="TABS.DOCUMENTS.value">
          <template slot="header">
            <label class="tabs-title spacing-px-10">
              {{ General.DOCUMENTS }}
            </label>
          </template>
          <ProcessDetailsDocumentContent
            v-if="process.id === id && TABS.DOCUMENTS.value === activeTab"
            :process="process"
            :isAllowToKnowledge="process.was_tramited && isUserResponsable"
            :userId="user.id"
            :isUserResponsable="isUserResponsable"
            :isAppendedProcess="isAppendedProcess"
            :isConfidential="isConfidential"
          />
        </b-tab-item>

        <b-tab-item :value="TABS.TIMELINE.value">
          <template slot="header">
            <label class="tabs-title spacing-px-10">
              {{ General.TIMELINE }}
            </label>
          </template>
          <TimelineTab :id="id" />
        </b-tab-item>

        <b-tab-item
          v-if="showMergeProcessTab"
          :value="TABS.MERGE_PROCESS.value"
        >
          <template slot="header">
            <label class="tabs-title spacing-px-10">
              {{ General.MERGE_PROCESS }}
            </label>
          </template>
          <template v-if="TABS.MERGE_PROCESS.value === activeTab">
            <MergeProcessTab v-if="showMergeProcessTab" />
          </template>
        </b-tab-item>
        <b-tab-item
          v-if="showMergeDocumentTab"
          :value="TABS.MERGE_DOCUMENTS.value"
        >
          <template slot="header">
            <label class="tabs-title spacing-px-10">
              {{ General.MERGE_DOCUMENTS }}
            </label>
          </template>
          <template v-if="TABS.MERGE_DOCUMENTS.value === activeTab">
            <MergeDocumentTab
              v-if="process.can_tramit && process.show_join_document_tab"
              :isUserResponsable="isUserResponsable"
              :nup="process.nup"
            />
          </template>
        </b-tab-item>
        <b-tab-item
          v-if="isPendingAllowed"
          :value="TABS.PENDING_DOCUMENTS.value"
        >
          <template slot="header">
            <span class="tabs-title spacing-px-10">
              {{ General.PENDING }}
            </span>
          </template>
          <PendingDocumentsTab
            v-if="TABS.PENDING_DOCUMENTS.value === activeTab"
          />
        </b-tab-item>
      </b-tabs>
    </div>
  </AppContent>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import AppContent from "@/layout/AppContent";

import ModalArchive from "@/views/App/ModalArchive";
import ModalAssignResponsible from "@/views/App/ModalAssignResponsible";
import ModalForwardProcess from "@/views/App/ModalForwardProcess.vue";
import DispatchProcessTab from "@/views/App/DispatchProcessTab";
import TimelineTab from "@/views/App/TimelineTab";

import MergeDocumentTab from "@/components/MergeDocumentTab";
import MergeProcessTab from "@/components/MergeProcessTab";
import PendingDocumentsTab from "@/components/PendingDocumentsTab";
import ProcessDownloadStep from "@/components/ProcessDownloadStep";

import { App, General } from "@/utils/constants";
import { NUP, applyMask } from "@/utils/protocols";
import {
  PROCESS_DETAILS_TABS as TABS,
  USER_PERMISSIONS as userPermissions,
  DOC_SIGNATURES_STATUS as DOC_SIGN,
  PROCESS_DOWNLOAD_STATUS as STATUS,
  SECRECY as secrecies
} from "@/utils/enumerate";

import ProcessDetailsDocumentContent from "./ProcessDetailsDocumentContent";
import ProcessData from "./ProcessData";
import ModalWarning from "@/views/App/ModalWarning.vue";
import ModalGiveBackOrigin from "@/views/App/ModalGiveBackOrigin.vue";
import ModalNewsDetail from "@/views/App/ModalNewsDetail.vue";
import { showErrorMessage } from "@/utils/messageUtility";
import { downloadFile } from "@/utils/fileUtility";

export default {
  name: "ProcessDetails",

  components: {
    AppContent,
    ProcessDetailsDocumentContent,
    ProcessData,
    MergeDocumentTab,
    MergeProcessTab,
    PendingDocumentsTab,
    DispatchProcessTab,
    TimelineTab
  },

  props: {
    id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      App,
      General,
      TABS,
      DOC_SIGN,
      userPermissions,
      animated: false,
      expanded: false,
      multiline: true,
      showAllDetails: false,
      showPDFTooltip: false,
      withdrawalClosed: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      process: "getCurrentProcess",
      bookmarks: "getBookmarks",
      processTime: "getProcessTime",
      getCapacitiesFiltered: "getCapacitiesFiltered",
      getCurrentTab: "getCurrentTabProcessDetails",
      hasPermission: "hasPermission",
      getCurrentJoint: "getCurrentJoint",
      joinFilesCount: "getJointFilesCount",
      isContrast: "isContrast",
      isMobile: "isMobile",
      mobileSize: "mobileSize",
      isConfidentialCheck: "isConfidentialCheck",
      getUpdatedDataStatus: "getUpdatedDataStatus",
      signatureRequests: "getSignatureRequests",
      socketGetDocumentToUpdate: "socketGetDocumentToUpdate",
      isAuthenticated: "isAuthenticated"
    }),

    hasOriginProcessAvailable() {
      return (
        this.hasPermission(userPermissions[`AUTHORIZE_DOE`]) ||
        this.hasPermission(userPermissions[`MANAGE_DOE`])
      );
    },

    inActualCapacity() {
      return (
        this.user.capacity.part_id === this.process.capacity.entityId &&
        this.user.capacity.capacity_id === this.process.capacity.id
      );
    },

    isUserResponsable() {
      return this.user.id === this.process.user_responsible_id;
    },

    isDispatchAllowed() {
      return (
        this.isUserResponsable &&
        this.user.capacity.capacity_id === this.process.capacity.id &&
        (!this.isConfidential ||
          (this.isConfidential && this.hasPermissionViewConfidential))
      );
    },

    secrecyName() {
      return (
        secrecies.find(secrecy => secrecy.id === this.process.secrecy)?.name ||
        ""
      );
    },

    isConfidential() {
      return this.isConfidentialCheck(this.process.secrecy);
    },

    isAppenderProcess() {
      return Boolean(this.process.process_join_set?.length);
    },

    isAppendedProcess() {
      return Boolean(this.process.process_join);
    },

    validateResponsable() {
      return (
        this.hasPermission(userPermissions["ADD_RESPONSIBLE"]) &&
        this.user.capacity?.part_id === this.process.capacity.entityId &&
        this.user.capacity?.capacity_id === this.process.capacity.id
      );
    },

    canArchiveOrUnarchive() {
      const permissionAllowed = this.process.is_archived
        ? this.hasPermission(userPermissions["UNARCHIVE_PROCESS"])
        : this.hasPermission(userPermissions["ARCHIVE_PROCESS"]) &&
          this.isUserResponsable;

      return (
        permissionAllowed &&
        !this.process.process_join &&
        this.process.can_tramit
      );
    },

    activeTab: {
      get() {
        return this.getCurrentTab.value;
      },
      set(newValue) {
        const oldValue = this.getCurrentTab.value;
        this.setCurrentTab(this.TABS[newValue].value);
        if (
          this.joinFilesCount &&
          oldValue !== newValue &&
          oldValue === this.TABS.MERGE_DOCUMENTS.value
        ) {
          this.showModalConfirmOut(confirm => {
            if (confirm === false)
              this.setCurrentTab(this.TABS.MERGE_DOCUMENTS.value);
          });
        }
      }
    },

    isProcessPortal() {
      return this.process.origin.id === -1;
    },

    isPendingAllowed() {
      return this.isUserResponsable && this.isProcessPortal;
    },

    showMessageWithdrawal() {
      return (
        this.isProcessPortal &&
        this.process.giveup_request &&
        this.canArchiveOrUnarchive &&
        !this.withdrawalClosed
      );
    },

    hasForwardPermission() {
      return this.hasPermission(userPermissions["FORWARD"]);
    },

    showDispatchTab() {
      return (
        this.isDispatchAllowed &&
        !this.isAppendedProcess &&
        this.process.can_tramit
      );
    },

    showDocumentsTab() {
      return this.canShowArchived && this.process.show_files;
    },

    canShowArchived() {
      return (
        !this.process.is_archived ||
        this.process.take_part_from_process ||
        this.hasPermission(userPermissions[`VIEW_RESTRICT_PROCESS`]) ||
        this.hasPermission(userPermissions[`INTERNAL_CONTROL`])
      );
    },

    showMergeProcessTab() {
      return this.process.can_tramit && this.process.show_join_process_tab;
    },

    showMergeDocumentTab() {
      return this.process.can_tramit && this.process.show_join_document_tab;
    },

    getSignatureHistory() {
      if (!this.id) return [];
      return (
        this.signatureRequests.filter(
          history => history.process_id === this.id
        ) || []
      );
    },

    isProcessOpennedExternal() {
      return (
        this.process.external_system_abbreviation && !this.process.can_tramit
      );
    },

    getViewTooltipLabelExternal() {
      return `${General.TEXTS.UNABLE_TO_VIEW_CONTENT} ${this.process.external_system_abbreviation}`;
    },

    getViewTooltipLabelExternalSystem() {
      return `${General.MESSAGES.WARNING_INTEGRATION_PROCESS_SYSTEM}: ${this.process.external_system_abbreviation}`;
    },

    tabHasDocument() {
      return [
        TABS.ORDERS.value,
        TABS.DOCUMENTS.value,
        TABS.MERGE_DOCUMENTS.value
      ].includes(this.activeTab);
    },

    hasPermissionViewConfidential() {
      return this.hasPermission(userPermissions["VIEW_CONFIDENTIAL_PROCESS"]);
    },

    hasPermissionAddConfidentialResponsible() {
      return this.hasPermission(
        userPermissions["ADD_CONFIDENTIAL_RESPONSIBLE"]
      );
    }
  },

  watch: {
    socketGetDocumentToUpdate: {
      async handler(value) {
        if (this.tabHasDocument && value.files_id?.length) {
          await this.requestProcessFiles(value);
          this.clearDocumentToUpdate();
        }
      },
      deep: true
    },

    getCurrentTab: {
      handler(tab) {
        this.checkTab(tab);
      },
      deep: true
    }
  },

  beforeRouteLeave(to, from, next) {
    if (this.isLoading) return;
    if (!this.isAuthenticated) return next();
    if (
      this.joinFilesCount &&
      this.activeTab === this.TABS.MERGE_DOCUMENTS.value
    ) {
      this.showModalConfirmOut(confirm => {
        if (confirm === false) next(false);
        else next();
      });
    } else next();
  },

  beforeMount() {
    this.configPage();
  },

  methods: {
    ...mapMutations({
      setCurrentTab: "setCurrentTabProcessDetails",
      clearDocumentToUpdate: "clearDocumentToUpdate",
      removeProcessDownload: "removeProcessDownload"
    }),

    ...mapActions({
      getProcess: "requestProcess",
      getProcessJoint: "getProcessJoint",
      getCompleteProcessFileStream: "getCompleteProcessFileStream",
      getJointFile: "getJointFile",
      bookmarkRequest: "bookmarkRequest",
      requestFile: "requestDataProcessFile",
      hasAutoDraftWritten: "hasAutoDraftWritten",
      fileTypesRequest: "fileTypesRequest",
      checkUserPermissionResponsible: "checkUserPermissionResponsible",
      signatureHistory: "getSignatureHistoryRequest",
      readSignature: "readSignature",
      requestCheckProcessDocumentJoint: "requestCheckProcessDocumentJoint",
      requestProcessFiles: "processFilesFilteredRequest",
      checkAndAddProcessDownlaod: "checkAndAddProcessDownlaod",
      getAllowedExtensions: "getAllowedExtensions"
    }),

    async initializeScreen() {
      this.isLoading = true;
      const results = await this.getProcess(this.id);
      this.isLoading = false;

      if (results.kind !== "ok") this.$router.push("/app/processos");
      else {
        const awaitRequests = [
          () => this.bookmarkRequest({ all: false }),
          this.getAllowedExtensions
        ];

        if (this.process.process_join)
          this.snackToast.open({
            message: General.TEXTS.PROCESS_APPEND,
            type: "is-warning",
            duration: 8000
          });

        awaitRequests.push(async () => {
          if (this.activeTab !== this.TABS.MERGE_DOCUMENTS.value) {
            await this.getProcessJoint(this.id);
            await this.getJointFile({
              jointId: this.getCurrentJoint.id,
              page: 1,
              paginator_count: 1,
              only_count: true
            });
          }
          if (this.joinFilesCount)
            this.snackToast.open({
              message: this.General.MESSAGES.IS_THERE_A_JOIN_DRAFT,
              type: "is-info",
              duration: 8000
            });
        });

        await Promise.allSettled(awaitRequests.map(fn => fn()));
        if (this.showDispatchTab || this.showDocumentsTab) {
          await this.fileTypesRequest({ state: true });
        }
        this.signatureHistory([this.id]);
      }
    },

    checkTab(currentTab) {
      const tabConditions = [
        { showTab: this.showDispatchTab, tab: this.TABS.ORDERS },
        { showTab: this.showDocumentsTab, tab: this.TABS.DOCUMENTS },
        { showTab: this.showMergeProcessTab, tab: this.TABS.MERGE_PROCESS },
        { showTab: this.showMergeDocumentTab, tab: this.TABS.MERGE_DOCUMENTS },
        { showTab: this.isPendingAllowed, tab: this.TABS.PENDING_DOCUMENTS }
      ];

      if (
        this.process &&
        tabConditions.some(
          condition =>
            !condition.showTab && currentTab.value === condition.tab.value
        )
      ) {
        this.setCurrentTab(this.TABS.BASIC_DATA.value);
      }
    },

    async configPage() {
      await this.initializeScreen();

      if (
        this.process &&
        !(this.process.was_tramited || this.isUserResponsable) &&
        this.isAppendedProcess
      )
        this.setCurrentTab(this.TABS.BASIC_DATA.value);

      this.checkTab(this.getCurrentTab);

      if (
        this.process &&
        this.process.is_archived &&
        !this.process.take_part_from_process &&
        this.getCurrentTab.value === this.TABS.DOCUMENTS.value
      )
        this.setCurrentTab(this.TABS.BASIC_DATA.value);
    },

    async checkSecrecyOnAssignResponsible() {
      const newProcess = [this.process];
      if (this.isConfidential) {
        const hasConfidentialPermission = this.validateConfidentialResponsible();
        if (!hasConfidentialPermission) return;

        const ids = newProcess.map(({ id }) => id);
        this.isLoading = true;
        const canChangeResponsible = await this.checkUserPermissionResponsible(
          ids
        );
        this.isLoading = false;
        if (!canChangeResponsible) return;
        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: `${this.General.WARNING}!`,
            message: this.General.MESSAGES.ALERT_PROCESS_SECRECY,
            invertOutline: true,
            confirm: this.showAssignResponsibleModal,
            buttons: [
              { label: General.YES, isConfirm: true, action: true },
              { label: General.NO, isConfirm: false, action: false }
            ]
          },
          hasModalCard: true,
          trapFocus: true
        });
      } else {
        this.showAssignResponsibleModal();
      }
    },

    showAssignResponsibleModal() {
      const newProcess = [this.process];
      this.$buefy.modal.open({
        parent: this,
        component: ModalAssignResponsible,
        props: {
          processList: newProcess,
          onClose: () => this.configPage()
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async checkDispathFilled(callback) {
      const result = await this.hasAutoDraftWritten({
        processId: this.process.id
      });
      if (result.has_auto_draft_files)
        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: `${this.General.WARNING}!`,
            message: `${this.General.MESSAGES.DISPATCH_FILLED}?`,
            confirm: callback,
            buttons: [
              { label: General.YES, isConfirm: true, action: true },
              { label: General.NO, isConfirm: false, action: false }
            ],
            invertOutline: true
          },
          hasModalCard: true,
          trapFocus: true
        });
      else callback();
    },

    async processHasJoin() {
      const resultsHasJoin = await this.requestCheckProcessDocumentJoint(
        this.process.id
      );

      if (resultsHasJoin.length) {
        let userListText = "";

        let message =
          resultsHasJoin.length === 1
            ? this.General.MESSAGES.DOCUMENT_JOIN_FILLED_SINGULAR
            : this.General.MESSAGES.DOCUMENT_JOIN_FILLED_PLURAL;

        resultsHasJoin.forEach((user, index) => {
          userListText += `${user.user_name} - ${user.organ}/${user.unit}`;
          if (index < resultsHasJoin.length - 2) userListText += ", ";
          else if (index == resultsHasJoin.length - 2) userListText += " e ";
        });

        message = message.replace("(USERS)", userListText);

        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: `${this.General.WARNING}!`,
            message: message,
            confirm: () => this.archiveProcess(false),
            buttons: [
              { label: General.YES, isConfirm: false, action: true },
              { label: General.NO, isConfirm: true, action: false }
            ]
          },
          hasModalCard: true,
          trapFocus: true
        });
      } else this.archiveProcess(false);
    },

    async archiveProcess(checkJoinFilled = true) {
      if (this.isConfidential) {
        const hasConfidentialPermission = this.validateConfidentialView(
          this.process.is_archived
            ? General.TEXTS.CONFIDENTIAL_DESARCHIVE_WARNING
            : General.TEXTS.CONFIDENTIAL_ARCHIVE_WARNING
        );
        if (!hasConfidentialPermission) return;
      }
      if (!this.process.is_archived) {
        if (checkJoinFilled) {
          await this.processHasJoin();
        } else {
          const results = await this.hasAutoDraftWritten({
            processId: this.process.id
          });

          if (results.has_auto_draft_written)
            this.$buefy.modal.open({
              parent: this,
              component: ModalWarning,
              props: {
                title: `${this.General.WARNING}!`,
                message: `${this.General.MESSAGES.DISPATCH_FILLED}?`,
                confirm: async () => this.showArchiveModal(),
                buttons: [
                  { label: General.YES, isConfirm: false, action: true },
                  { label: General.NO, isConfirm: true, action: false }
                ]
              },
              hasModalCard: true,
              trapFocus: true
            });
          else this.showArchiveModal();
        }
      } else this.showArchiveModal();
    },

    showArchiveModal() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalArchive,
        props: {
          unarchived: this.process.is_archived,
          listProcess: [this.process],
          onClose: () => {
            this.setCurrentTab(this.TABS.BASIC_DATA.value);
            this.initializeScreen();
          }
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    listProcesses() {
      this.$router.go(-1);
    },

    applyMaskNUP(value) {
      return applyMask(NUP, value);
    },

    openModalForwardProcess() {
      if (!this.process.can_tramit)
        return showErrorMessage(General.MESSAGES.WARNING_INTEGRATION_PROCESS);
      this.$buefy.modal.open({
        parent: this,
        component: ModalForwardProcess,
        props: {
          listProcess: [this.process],
          callback: () => this.configPage()
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    openModalReturnToOrigin() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalGiveBackOrigin,
        props: {
          listProcess: [this.process],
          callback: this.initializeScreen
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    openModalDetail() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalNewsDetail,
        props: {
          history: this.getSignatureHistory
        },
        canCancel: ["escape", "x", "outside"],
        onCancel: this.signatureHistory([this.id]),
        hasModalCard: true,
        trapFocus: true
      });
      this.readSignature(this.id);
    },

    async newDetailReaded() {
      await this.readSignature(this.id);
      this.signatureHistory([this.id]);
    },

    closeWithdrawalMessage() {
      this.withdrawalClosed = true;
    },

    setShowPDFTooltip(state) {
      this.showPDFTooltip = state;
    },

    validateConfidentialResponsible() {
      if (!this.hasPermissionAddConfidentialResponsible) {
        this.snackToast.open({
          message: this.General.TEXTS.CONFIDENTIAL_RESPONSIBLE_WARNING,
          position: "is-top",
          type: "is-warning",
          duration: 8000
        });
        return false;
      }
      return true;
    },

    actionsList() {
      return [
        {
          label: this.General.TO_VIEW,
          icon: "folder-search",
          validated: this.canShowArchived,
          callback: () => this.getCompleteProcessFileStream(this.process.id)
        },
        {
          label: this.process.is_archived
            ? this.General.UNARCHIVE_PROCESS
            : this.General.ARCHIVE_PROCESS,
          icon: this.process.is_archived
            ? "archive-arrow-up"
            : "archive-arrow-down",
          validated: this.canArchiveOrUnarchive,
          iconType: this.inActualCapacity ? null : "is-grey-light",
          callback: () =>
            this.inActualCapacity
              ? this.archiveProcess()
              : this.snackToast.open({
                  message: this.General.TEXTS.UNABLE_TO_UNARCHIVED_SINGULAR,
                  position: "is-top",
                  type: "is-danger",
                  duration: 8000
                })
        },
        {
          label: this.process.user_responsible_id
            ? General.CHANGE_RESPONSIBLE
            : General.ASSIGN_RESPONSIBLE,
          icon: "account-arrow-right",
          validated:
            this.validateResponsable &&
            !this.isAppendedProcess &&
            !this.process.is_archived &&
            this.process.can_tramit,
          callback: () => this.checkSecrecyOnAssignResponsible()
        },
        {
          label: General.FORWARD,
          icon: "send",
          validated:
            this.hasForwardPermission && !this.isProcessOpennedExternal,
          callback: () => this.checkDispathFilled(this.openModalForwardProcess)
        },
        {
          label: this.General.TEXTS.PROCESSED_ORIGIN,
          icon: "send",
          validated: this.hasOriginProcessAvailable && this.process.can_tramit,
          permission: this.inActualCapacity,
          customPermissionMessage: General.TEXTS.NOT_PROCESS_ORIGIN,
          callback: () => this.openModalReturnToOrigin()
        },
        {
          label: this.General.OCCURENCY_FILE_PLURAL,
          icon: "file-search",
          validated: this.canShowArchived,
          callback: () => this.checkIsConfidential(this.downloadOccurrency)
        },
        {
          label: this.App.ProcessViewer.COMPLETE_FILE,
          icon: "file-pdf-box",
          validated: this.showDocumentsTab,
          callback: () =>
            this.checkIsConfidential(this.putProcessInQueue, false)
        }
      ];
    },

    showModalConfirmOut(callback) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.WARNING,
          message: this.General.TEXTS.CONFIRM_JOINT_DOCUMENT_GO_OUT,
          confirm: async () => {
            callback();
          },
          deny: () => {
            callback(false);
          },
          buttons: [
            { label: General.YES, isConfirm: true, action: true },
            { label: General.NO, isConfirm: false, action: false }
          ],
          isMessageCentered: true,
          invertOutline: true
        },
        hasModalCard: true,
        trapFocus: true,
        canCancel: false
      });
    },

    async downloadOccurrency() {
      this.isLoading = true;
      const result = await this.requestFile({
        fileIds: [{ process_file_id: this.process.occurrency_id }],
        processId: this.process.id
      });
      if (result) {
        const [{ url_file, attachment_name }] = result;
        downloadFile({
          url: url_file,
          attachment_name: `${attachment_name} ${this.process.nup}.pdf`
        });
      }
      this.isLoading = false;
    },

    validateConfidentialView(
      message = this.General.TEXTS.CONFIDENTIAL_VIEW_WARNING
    ) {
      if (!this.hasPermissionViewConfidential) {
        this.snackToast.open({
          message,
          position: "is-top",
          type: "is-warning",
          duration: 8000
        });
        return false;
      }
      return true;
    },

    checkIsConfidential(callback = () => null, doValidation = true) {
      if (this.isConfidential) {
        if (doValidation) {
          const hasConfidentialPermission = this.validateConfidentialView();
          if (!hasConfidentialPermission) return;
        }

        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: `${this.General.WARNING}!`,
            message: this.General.MESSAGES.ALERT_PROCESS_SECRECY,
            invertOutline: true,
            confirm: callback,
            buttons: [
              { label: General.YES, isConfirm: true, action: true },
              { label: General.NO, isConfirm: false, action: false }
            ]
          },
          hasModalCard: true,
          trapFocus: true
        });
      } else {
        callback();
      }
    },

    async putProcessInQueue() {
      const success = await this.checkAndAddProcessDownlaod({
        processId: this.id,
        status: STATUS.PENDING,
        lastState: Date.now()
      });
      this.snackQueue.open({
        id: `download_process:${this.id}`,
        parent: this,
        props: {
          processId: this.id,
          nup: this.process.nup
        },
        component: ProcessDownloadStep,
        closeAction: () => this.removeProcessDownload(this.id)
      });
      if (!success)
        this.snackToast.open({
          message: this.General.MESSAGES.PROCESSE_ALREADY_ADD,
          type: "is-danger",
          duration: 8000
        });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.wrapper-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 20px;
}

.wrapper-header div.header-item-right {
  padding: 0 20px;
  flex-grow: 2;
  @media screen and (max-width: $tablet) {
    padding-right: 5px;
  }
}

.wrapper-header div.header-item-actions {
  padding: 0 20px;
  width: 60%;
}

.new-action {
  display: flex;
  justify-content: space-between;
  background-color: #eaf5e7;
  padding: 5px;
  margin-bottom: 10px;

  &.red {
    background-color: #f7dada;
  }
}
</style>
